import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
// Google OAuth via manual redirect (no library popup issues)
import { useAuth } from './AuthContext'
import { useTheme } from '../context/ThemeContext'
import { Ic } from '../components/ui'
import { apiUrl } from '../shared/config/apiBase'
import { GOOGLE_CLIENT_ID } from './config/constants'

const HAS_GOOGLE = Boolean(GOOGLE_CLIENT_ID)

if (!HAS_GOOGLE && process.env.NODE_ENV === 'development') {
  console.warn('[Auth] REACT_APP_GOOGLE_CLIENT_ID не загружен — перезапустите npm start')
}

/* ─── Password strength ─── */
function calcStrength(pw) {
  if (!pw) return 0
  let s = 0
  if (pw.length >= 6)  s++
  if (pw.length >= 10) s++
  if (/[A-ZА-Я]/.test(pw)) s++
  if (/[0-9]/.test(pw))     s++
  if (/[^a-zA-Zа-яА-Я0-9]/.test(pw)) s++
  return Math.min(4, s)
}
const PW_COLORS = ['', '#EF4444', '#F97316', '#EAB308', '#22C55E']
const PW_LABELS = ['', 'Слабый', 'Средний', 'Хороший', 'Надёжный']

function PwStrength({ pw, dark }) {
  const lvl = calcStrength(pw)
  if (!pw) return null
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display:'flex', gap:3, marginBottom:5 }}>
        {[1,2,3,4].map(i => (
          <div key={i} style={{
            flex:1, height:3, borderRadius:2,
            background: i <= lvl ? PW_COLORS[lvl] : dark ? 'rgba(255,255,255,.1)' : 'rgba(10,32,40,.1)',
            transition: 'background .3s',
          }}/>
        ))}
      </div>
      {lvl > 0 && (
        <span style={{ fontSize:11, color: PW_COLORS[lvl], fontWeight:700, letterSpacing:'.04em' }}>
          {PW_LABELS[lvl]}
        </span>
      )}
    </div>
  )
}

/* ─── Step indicator ─── */
function StepDots({ step, dark, line }) {
  const COLOR = '#0FA3B1'
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:0, marginBottom:20 }}>
      {[1,2].map((n,i) => {
        const done    = step >= n
        const current = step === n
        return (
          <React.Fragment key={n}>
            {i > 0 && (
              <div style={{
                width: 48, height: 2, borderRadius:1, margin:'0 6px',
                background: done ? COLOR : dark ? 'rgba(255,255,255,.1)' : 'rgba(10,32,40,.1)',
                transition:'background .4s',
              }}/>
            )}
            <div style={{
              width:30, height:30, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center',
              fontSize:12, fontWeight:800, transition:'all .35s',
              background: done
                ? `linear-gradient(135deg,#1FC8D6,#0A7B86)`
                : dark ? 'rgba(255,255,255,.06)' : 'rgba(10,32,40,.07)',
              border: `2px solid ${done ? '#0FA3B1' : dark ? 'rgba(255,255,255,.15)' : 'rgba(10,32,40,.15)'}`,
              color: done ? '#fff' : dark ? 'rgba(255,255,255,.3)' : 'rgba(10,32,40,.35)',
              boxShadow: current ? '0 0 0 3px rgba(15,163,177,.2)' : 'none',
            }}>{n}</div>
          </React.Fragment>
        )
      })}
    </div>
  )
}

/* ─── Google button — popup OAuth (BroadcastChannel, COOP-safe) ─── */
function GoogleLoginBtn({ isLogin, loading, dark, line, text }) {
  const handleGooglePopup = () => {
    if (!GOOGLE_CLIENT_ID) return
    // state starts with 'popup_' so the popup can detect it's in popup mode
    const state = 'popup_' + Math.random().toString(36).slice(2, 10)
    sessionStorage.setItem('google_oauth_state', state)
    const params = new URLSearchParams({
      client_id: GOOGLE_CLIENT_ID,
      redirect_uri: 'https://welland-create.kz/auth',
      response_type: 'token',
      scope: 'openid email profile',
      state,
    })
    const url = `https://accounts.google.com/o/oauth2/v2/auth?${params}`
    const w = 500, h = 640
    const left = Math.round(window.screen.width / 2 - w / 2)
    const top = Math.round(window.screen.height / 2 - h / 2)
    const popup = window.open(url, 'google_oauth', `width=${w},height=${h},left=${left},top=${top},toolbar=0,menubar=0,location=0,scrollbars=0`)
    if (!popup) window.location.href = url // fallback if popup blocked
  }
  return (
    <button
      onClick={handleGooglePopup}
      disabled={loading}
      style={{
        width:'100%', padding:'12px 16px',
        border:`1px solid ${line}`,
        borderRadius:12,
        background: dark ? 'rgba(255,255,255,.06)' : '#F8FAFB',
        color:text, fontSize:14.5, fontWeight:600,
        cursor: loading ? 'not-allowed' : 'pointer',
        display:'flex', alignItems:'center', justifyContent:'center', gap:10,
        fontFamily:"'Manrope',sans-serif", transition:'border-color .2s, background .2s',
        opacity: loading ? .6 : 1,
      }}
      onMouseEnter={e => { if (!loading) { e.currentTarget.style.borderColor = '#0FA3B1'; e.currentTarget.style.background = dark ? 'rgba(255,255,255,.1)' : '#EFF9FA' } }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = line; e.currentTarget.style.background = dark ? 'rgba(255,255,255,.06)' : '#F8FAFB' }}>
      <Ic n="google" s={18}/>
      {isLogin ? 'Продолжить с Google' : 'Зарегистрироваться через Google'}
    </button>
  )
}


/* ─── Input field ─── */
function Field({ label, value, onChange, placeholder, type='text', id, dark, autocomplete }) {
  const [focus, setFocus] = useState(false)
  const bdr = dark ? 'rgba(255,255,255,.1)' : 'rgba(10,32,40,.12)'
  return (
    <div>
      {label && (
        <label htmlFor={id} style={{ display:'block', fontSize:11, fontWeight:700, color: dark ? 'rgba(255,255,255,.5)' : 'rgba(10,32,40,.5)', letterSpacing:'.1em', textTransform:'uppercase', marginBottom:6, fontFamily:"'JetBrains Mono',monospace" }}>
          {label}
        </label>
      )}
      <input
        id={id} type={type} value={value} onChange={onChange} placeholder={placeholder}
        autoComplete={autocomplete || (type==='email' ? 'email' : type==='tel' ? 'tel' : undefined)}
        style={{
          width:'100%', padding:'12px 16px',
          background: dark ? 'rgba(255,255,255,.06)' : 'rgba(10,32,40,.05)',
          border:`1px solid ${focus ? '#0FA3B1' : bdr}`,
          borderRadius:11, color: dark ? '#fff' : '#0A2028',
          fontFamily:"'Manrope',sans-serif", fontSize:16,
          outline:'none', transition:'border-color .2s, box-shadow .2s',
          boxSizing:'border-box',
          boxShadow: focus ? '0 0 0 3px rgba(15,163,177,.12)' : 'none',
        }}
        onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
      />
    </div>
  )
}

/* ─── Password field ─── */
function PwField({ label, value, onChange, placeholder, id, dark, autocomplete='current-password', showStrength=false }) {
  const [show,  setShow]  = useState(false)
  const [focus, setFocus] = useState(false)
  const bdr = dark ? 'rgba(255,255,255,.1)' : 'rgba(10,32,40,.12)'
  return (
    <div>
      {label && (
        <label htmlFor={id} style={{ display:'block', fontSize:11, fontWeight:700, color: dark ? 'rgba(255,255,255,.5)' : 'rgba(10,32,40,.5)', letterSpacing:'.1em', textTransform:'uppercase', marginBottom:6, fontFamily:"'JetBrains Mono',monospace" }}>
          {label}
        </label>
      )}
      <div style={{ position:'relative' }}>
        <input
          id={id} type={show ? 'text' : 'password'} value={value} onChange={onChange} placeholder={placeholder}
          autoComplete={autocomplete}
          style={{
            width:'100%', padding:'12px 44px 12px 16px',
            background: dark ? 'rgba(255,255,255,.06)' : 'rgba(10,32,40,.05)',
            border:`1px solid ${focus ? '#0FA3B1' : bdr}`,
            borderRadius:11, color: dark ? '#fff' : '#0A2028',
            fontFamily:"'Manrope',sans-serif", fontSize:16,
            outline:'none', transition:'border-color .2s, box-shadow .2s', boxSizing:'border-box',
            boxShadow: focus ? '0 0 0 3px rgba(15,163,177,.12)' : 'none',
          }}
          onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
        />
        <button type="button" onClick={() => setShow(s => !s)} style={{
          position:'absolute', right:13, top:'50%', transform:'translateY(-50%)',
          background:'none', border:'none', cursor:'pointer',
          color: dark ? 'rgba(255,255,255,.35)' : 'rgba(10,32,40,.35)', padding:0, transition:'color .2s',
        }}
        onMouseEnter={e => e.currentTarget.style.color = '#0FA3B1'}
        onMouseLeave={e => e.currentTarget.style.color = dark ? 'rgba(255,255,255,.35)' : 'rgba(10,32,40,.35)'}>
          <Ic n={show ? 'eyeoff' : 'eye'} s={15}/>
        </button>
      </div>
      {showStrength && <PwStrength pw={value} dark={dark}/>}
    </div>
  )
}

/* ─── Divider ─── */
function Divider({ dark }) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:12, margin:'4px 0' }}>
      <div style={{ flex:1, height:1, background: dark ? 'rgba(255,255,255,.08)' : 'rgba(10,32,40,.1)' }}/>
      <span style={{ fontSize:12, color: dark ? 'rgba(255,255,255,.3)' : 'rgba(10,32,40,.38)', fontWeight:500 }}>или</span>
      <div style={{ flex:1, height:1, background: dark ? 'rgba(255,255,255,.08)' : 'rgba(10,32,40,.1)' }}/>
    </div>
  )
}

/* ─── Error box ─── */
function ErrBox({ msg }) {
  if (!msg) return null
  return (
    <div style={{
      padding:'10px 14px', background:'rgba(239,68,68,.1)', border:'1px solid rgba(239,68,68,.3)',
      borderRadius:9, color:'#EF4444', fontSize:13,
      display:'flex', alignItems:'center', gap:8,
    }}>
      <Ic n="info" s={13}/>{msg}
    </div>
  )
}

/* ─── Submit button ─── */
function SubmitBtn({ loading, success, label, loadingLabel }) {
  return (
    <button type="submit" disabled={loading || success} style={{
      width:'100%', padding:'13px', borderRadius:12, border:'none',
      background: success
        ? 'linear-gradient(135deg,#22C55E,#16A34A)'
        : 'linear-gradient(135deg,#1FC8D6,#0FA3B1,#0A7B86)',
      color:'#fff', fontSize:15, fontWeight:700,
      cursor: (loading || success) ? 'not-allowed' : 'pointer',
      fontFamily:"'Manrope',sans-serif",
      boxShadow: success
        ? '0 6px 20px rgba(34,197,94,.38)'
        : '0 6px 22px rgba(15,163,177,.35)',
      transition:'background .35s, box-shadow .35s, transform .15s',
      opacity: loading ? .7 : 1,
      display:'flex', alignItems:'center', justifyContent:'center', gap:8,
      letterSpacing:'-.01em',
    }}
    onMouseEnter={e => { if (!loading && !success) e.currentTarget.style.transform='translateY(-1px)' }}
    onMouseLeave={e => { e.currentTarget.style.transform='none' }}>
      {success ? (
        <>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" fill="rgba(255,255,255,.2)"/>
            <path d="M7 12.5l3.5 3.5 6.5-7" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Готово!
        </>
      ) : loading ? (
        <>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ animation:'spin 1s linear infinite' }}>
            <circle cx="12" cy="12" r="9" stroke="rgba(255,255,255,.3)" strokeWidth="2.5"/>
            <path d="M12 3a9 9 0 0 1 9 9" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
          </svg>
          {loadingLabel}
        </>
      ) : label}
    </button>
  )
}

/* ─── Trust badges ─── */
function TrustBadges({ dark }) {
  const muted = dark ? 'rgba(255,255,255,.28)' : 'rgba(10,32,40,.32)'
  const items = [
    { icon:'lock', label:'Данные защищены' },
    { icon:'star', label:'Бесплатно' },
    { icon:'shield', label:'Без спама' },
  ]
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:16, flexWrap:'wrap' }}>
      {items.map(({ icon, label }) => (
        <div key={label} style={{ display:'flex', alignItems:'center', gap:5, color:muted }}>
          <Ic n={icon} s={12}/>
          <span style={{ fontSize:11.5, fontWeight:600 }}>{label}</span>
        </div>
      ))}
    </div>
  )
}

/* ─── Main auth page ─── */
export default function AuthPage() {
  const { dark, toggle } = useTheme()
  const authCtx = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const from = location.state?.from?.pathname || '/dashboard'

  useEffect(() => {
    // Skip redirect while OAuth hash is being processed in this window
    if (window.location.hash.includes('access_token=')) return
    if (!authCtx.loading && authCtx.isAuthenticated) {
      navigate(from, { replace: true })
    }
  }, [authCtx.isAuthenticated, authCtx.loading, navigate, from])

  // Handle Google OAuth redirect (runs in popup or in main window as fallback)
  useEffect(() => {
    const hash = window.location.hash
    if (!hash || !hash.includes('access_token=')) return
    const params = new URLSearchParams(hash.slice(1))
    const token = params.get('access_token')
    window.history.replaceState(null, '', '/auth')
    if (!token) return

    // Notify main window via BroadcastChannel (works regardless of COOP/opener state)
    try {
      const bc = new BroadcastChannel('google_oauth')
      bc.postMessage({ type: 'google_oauth', token })
      bc.close()
    } catch (_) {}

    // Try to close — works only for script-opened popups
    window.close()

    // If window.close() didn't close us, we're the main window (popup blocked → redirect fallback)
    // BroadcastChannel doesn't deliver to sender, so process token directly here
    setTimeout(() => {
      setLoading(true); setErr('')
      fetch(apiUrl('/api/auth/google'), {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      })
        .then(r => r.json().then(d => ({ ok: r.ok, d })))
        .then(({ ok, d }) => {
          if (!ok) throw new Error(d.message || 'Ошибка Google авторизации')
          onSuccess(d.token, d.user)
        })
        .catch(e => { setErr(e.message); setLoading(false) })
    }, 300)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Receive token from Google OAuth popup via BroadcastChannel (COOP-safe)
  useEffect(() => {
    let bc
    try {
      bc = new BroadcastChannel('google_oauth')
      bc.onmessage = (event) => {
        if (event.data?.type !== 'google_oauth' || !event.data.token) return
        const { token } = event.data
        setLoading(true); setErr('')
        fetch(apiUrl('/api/auth/google'), {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        })
          .then(r => r.json().then(d => ({ ok: r.ok, d })))
          .then(({ ok, d }) => {
            if (!ok) throw new Error(d.message || 'Ошибка Google авторизации')
            onSuccess(d.token, d.user)
          })
          .catch(e => { setErr(e.message); setLoading(false) })
      }
    } catch (_) {}
    return () => { try { bc?.close() } catch (_) {} }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /* mode: 'login' | 'register' | 'reg2' */
  const [mode,    setMode]    = useState('login')
  const [email,   setEmail]   = useState('')
  const [pass,    setPass]    = useState('')
  const [pass2,   setPass2]   = useState('')
  const [first,   setFirst]   = useState('')
  const [last,    setLast]    = useState('')
  const [loading, setLoading] = useState(false)
  const [err,     setErr]     = useState('')
  const [success, setSuccess] = useState(false)

  /* colors */
  const bg   = dark ? '#010810' : '#F0F5F9'
  const card = dark ? '#05111F' : '#FFFFFF'
  const line = dark ? 'rgba(255,255,255,.1)' : 'rgba(10,32,40,.1)'
  const text = dark ? 'rgba(255,255,255,.85)' : 'rgba(10,32,40,.9)'
  const muted = dark ? 'rgba(255,255,255,.42)' : 'rgba(10,32,40,.48)'

  const onSuccess = useCallback(async (token, user) => {
    setSuccess(true)
    await authCtx.login(token, user ? { user } : undefined)
    setTimeout(() => navigate(from, { replace:true }), 700)
  }, [authCtx, navigate, from])

  const handleLogin = async (e) => {
    e?.preventDefault()
    if (!email || !pass) { setErr('Введите email и пароль'); return }
    setLoading(true); setErr('')
    try {
      const res  = await fetch(apiUrl('/api/auth/login'), {
        method:'POST', credentials:'include',
        headers:{ 'Content-Type':'application/json' },
        body: JSON.stringify({ email: email.trim(), password: pass }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.message || 'Ошибка входа')
      onSuccess(data.token)
    } catch (e) { setErr(e.message); setLoading(false) }
  }

  const isValidEmail = v => /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(v.trim())

  const BAD_WORDS = [
    'хуй','хуя','хуе','хуи','хую','хуём','нахуй','захуй','похуй','хуёв',
    'пизд','пизда','пизде','пиздё','пизду','пиздой','пизды',
    'ёб','еб','ёба','еба','ёбан','ебан','ёбл','ебл','заёб','заеб','наёб','наеб','выёб','выеб','проёб','проеб',
    'блядь','бляди','блядю','блядей','блядск','блядун','бля','блять',
    'ёбнут','ёбнул','ёбнет','мудак','мудил','мудаков','мудачок',
    'пидор','пидар','пидер','пидорас','залупа','залупу','залупой',
    'манда','мандой','мандавошк','сука','суки','суке','суку','сукой',
    'шлюх','шлюха','шлюхе','шлюхи','шлюху','ублюдок','ублюдк',
    'долбоёб','долбоеб','долбоёбk','ёбарь','ебарь','гандон','гондон',
    'чмо','чмошник','дрочить','дрочил','дрочи','дроч',
    'fuck','fucker','fucking','fucked','fucks','shit','shits','shitting','shitty',
    'bitch','bitches','bitching','cunt','cunts','cock','cocks','dick','dicks',
    'ass','asses','asshole','assholes','whore','whores','bastard','bastards',
    'nigger','nigga','faggot','fag',
  ]
  const hasBadWord = v => {
    const n = v.toLowerCase().replace(/[^a-zа-яёa-z0-9]/gi, '')
    return BAD_WORDS.some(w => n.includes(w))
  }

  const handleReg1 = async (e) => {
    e?.preventDefault()
    if (!email) { setErr('Введите email'); return }
    if (!isValidEmail(email)) { setErr('Введите корректный email (например: name@mail.ru)'); return }
    setLoading(true); setErr('')
    try {
      const res  = await fetch(apiUrl('/api/auth/check-email'), {
        method:'POST', credentials:'include',
        headers:{ 'Content-Type':'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      })
      const data = await res.json()
      if (data.exists) { setErr('Пользователь с таким email уже существует'); return }
      setMode('reg2')
    } catch { setErr('Не удалось проверить email. Проверьте соединение.') }
    finally { setLoading(false) }
  }

  const handleReg2 = async (e) => {
    e?.preventDefault()
    if (!first || !pass) { setErr('Заполните обязательные поля'); return }
    if (hasBadWord(first) || hasBadWord(last)) { setErr('Имя содержит недопустимые слова'); return }
    if (pass !== pass2) { setErr('Пароли не совпадают'); return }
    if (pass.length < 6) { setErr('Пароль минимум 6 символов'); return }
    setLoading(true); setErr('')
    try {
      const name = [first.trim(), last.trim()].filter(Boolean).join(' ')
      const res  = await fetch(apiUrl('/api/auth/register'), {
        method:'POST', credentials:'include',
        headers:{ 'Content-Type':'application/json' },
        body: JSON.stringify({ name, email: email.trim(), password: pass }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.message || 'Ошибка регистрации')
      onSuccess(data.token, data.user)
    } catch (e) { setErr(e.message); setLoading(false) }
  }


  const isLogin = mode === 'login'
  const isReg2  = mode === 'reg2'
  const isReg   = mode === 'register'
  const step    = isReg2 ? 2 : 1

  return (
    <div style={{
      minHeight:'100vh', backgroundColor:bg,
      display:'flex', alignItems:'center', justifyContent:'center',
      padding:24, fontFamily:"'Manrope',system-ui,sans-serif",
      WebkitFontSmoothing:'antialiased',
      backgroundImage: dark
        ? 'radial-gradient(ellipse 80% 60% at 50% 0%,rgba(15,163,177,.14) 0%,transparent 70%),linear-gradient(rgba(31,200,214,.055) 1px,transparent 1px),linear-gradient(90deg,rgba(31,200,214,.055) 1px,transparent 1px)'
        : 'radial-gradient(ellipse 80% 60% at 50% 0%,rgba(15,163,177,.09) 0%,transparent 70%),linear-gradient(rgba(10,123,134,.07) 1px,transparent 1px),linear-gradient(90deg,rgba(10,123,134,.07) 1px,transparent 1px)',
      backgroundSize:'auto,54px 54px,54px 54px',
      backgroundAttachment:'local',
    }}>
      {/* Spinner CSS */}
      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>

      <div style={{ width:'100%', maxWidth:460, position:'relative' }}>

        {/* Theme toggle */}
        <button onClick={toggle} style={{
          position:'absolute', top:-48, right:0,
          background: dark ? 'rgba(255,255,255,.07)' : 'rgba(10,32,40,.07)',
          border:`1px solid ${line}`, borderRadius:999,
          padding:'6px 14px', color:text, fontSize:12.5, fontWeight:600,
          cursor:'pointer', fontFamily:"'Manrope',sans-serif",
          display:'flex', alignItems:'center', gap:6, transition:'border-color .2s',
        }}
        onMouseEnter={e => e.currentTarget.style.borderColor = '#0FA3B1'}
        onMouseLeave={e => e.currentTarget.style.borderColor = line}>
          <Ic n={dark ? 'sun' : 'moon'} s={14}/>{dark ? 'Светлая тема' : 'Тёмная тема'}
        </button>

        {/* Card */}
        <div style={{
          background: card,
          borderRadius:22,
          border:`1px solid ${line}`,
          boxShadow: dark
            ? '0 28px 80px rgba(0,0,0,.65), 0 0 0 1px rgba(31,200,214,.07), inset 0 1px 0 rgba(255,255,255,.05)'
            : '0 8px 48px rgba(10,32,40,.12), 0 0 0 1px rgba(15,163,177,.1), inset 0 1px 0 rgba(255,255,255,.9)',
          overflow:'hidden',
          transition:'box-shadow .3s',
        }}>

          {/* Top accent gradient bar */}
          <div style={{
            height:3,
            background:'linear-gradient(90deg,#1FC8D6 0%,#0FA3B1 45%,#0A7B86 75%,rgba(10,123,134,.3) 100%)',
          }}/>

          {/* Header */}
          <div style={{ padding:'32px 36px 0', position:'relative' }}>

            {/* Close */}
            <button onClick={() => navigate('/')} style={{
              position:'absolute', top:18, right:18,
              width:32, height:32, borderRadius:9,
              background: dark ? 'rgba(255,255,255,.07)' : 'rgba(10,32,40,.06)',
              border:`1px solid ${line}`, color:muted,
              cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center',
              transition:'all .2s',
            }}
            onMouseEnter={e => { e.currentTarget.style.color = text; e.currentTarget.style.borderColor = '#0FA3B1' }}
            onMouseLeave={e => { e.currentTarget.style.color = muted; e.currentTarget.style.borderColor = line }}>
              <Ic n="x" s={14}/>
            </button>

            {/* Logo */}
            <div style={{ display:'flex', justifyContent:'center', alignItems:'center', gap:11, marginBottom:22 }}>
              <img src="/logo.png" alt="Welland" style={{ width:44, height:44, objectFit:'contain', flexShrink:0 }}/>
              <span style={{ fontFamily:'var(--font-main)', display:'inline-flex', alignItems:'baseline', gap:7 }}>
                <span style={{ fontWeight:900, fontSize:18, letterSpacing:'0.06em', color: dark ? '#FFFFFF' : '#0A2028' }}>WELLAND</span>
                <span style={{ fontWeight:600, fontSize:13, letterSpacing:'0.18em', color:'#0FA3B1' }}>CREATE</span>
              </span>
            </div>

            {/* Step indicator (only for registration) */}
            {(isReg || isReg2) && (
              <StepDots step={step} dark={dark} line={line}/>
            )}

            {isReg2 ? (
              <>
                <button onClick={() => { setMode('register'); setErr(''); setFirst(''); setLast(''); setPass(''); setPass2(''); setSuccess(false) }} style={{
                  background:'none', border:'none', color:'#0FA3B1', fontSize:13, fontWeight:600,
                  cursor:'pointer', fontFamily:"'Manrope',sans-serif", padding:0, marginBottom:14,
                  display:'flex', alignItems:'center', gap:5, transition:'opacity .2s',
                }}
                onMouseEnter={e => e.currentTarget.style.opacity='.7'}
                onMouseLeave={e => e.currentTarget.style.opacity='1'}>
                  ← Назад
                </button>
                <h1 style={{ fontSize:20, fontWeight:800, color:text, marginBottom:4, letterSpacing:'-.02em' }}>Придумайте пароль</h1>
                <p style={{ fontSize:13, color:muted, marginBottom:0 }}>
                  Аккаунт для: <span style={{ color:'#0FA3B1', fontWeight:600 }}>{email}</span>
                </p>
              </>
            ) : (
              <>
                <h1 style={{ fontSize:22, fontWeight:800, color:text, margin:'0 0 4px', textAlign:'center', letterSpacing:'-.025em' }}>
                  {isLogin ? 'Добро пожаловать!' : 'Создать аккаунт'}
                </h1>
                <p style={{ fontSize:13.5, color:muted, textAlign:'center', marginBottom:0 }}>
                  {isLogin ? 'Войдите или зарегистрируйтесь' : 'Создайте аккаунт для сохранения проектов'}
                </p>
              </>
            )}
          </div>

          {/* Body */}
          <div style={{ padding:'24px 36px 28px', display:'flex', flexDirection:'column', gap:14 }}>

            {/* Google (not on reg2) */}
            {!isReg2 && HAS_GOOGLE && (
              <>
                <GoogleLoginBtn
                  loading={loading} dark={dark} isLogin={isLogin} line={line} text={text}
                />
                <Divider dark={dark}/>
              </>
            )}

            {!isReg2 && <ErrBox msg={err}/>}

            {/* Login form */}
            {isLogin && (
              <form onSubmit={handleLogin} style={{ display:'flex', flexDirection:'column', gap:12 }}>
                <Field value={email} onChange={e => setEmail(e.target.value)} placeholder="Email адрес" type="email" id="auth-email" dark={dark}/>
                <div>
                  <PwField value={pass} onChange={e => setPass(e.target.value)} placeholder="Пароль" id="auth-pass" dark={dark}/>
                  <div style={{ display:'flex', justifyContent:'flex-end', marginTop:6 }}>
                    <button type="button" onClick={() => navigate('/reset-password')} style={{
                      background:'none', border:'none', color:'#0FA3B1',
                      fontSize:12.5, fontWeight:600, cursor:'pointer',
                      fontFamily:"'Manrope',sans-serif", padding:0, transition:'opacity .2s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.opacity='.7'}
                    onMouseLeave={e => e.currentTarget.style.opacity='1'}>
                      Забыли пароль?
                    </button>
                  </div>
                </div>
                <SubmitBtn loading={loading} success={success} label="Войти в аккаунт" loadingLabel="Вход..."/>
              </form>
            )}

            {/* Register step 1 */}
            {isReg && (
              <form onSubmit={handleReg1} style={{ display:'flex', flexDirection:'column', gap:12 }}>
                <Field value={email} onChange={e => { setEmail(e.target.value); setErr('') }} placeholder="Email адрес" type="email" id="reg-email" dark={dark}/>
                <SubmitBtn loading={loading} success={success} label="Продолжить →" loadingLabel="Проверка..."/>
              </form>
            )}

            {/* Register step 2 */}
            {isReg2 && (
              <form onSubmit={handleReg2} style={{ display:'flex', flexDirection:'column', gap:12 }}>
                <ErrBox msg={err}/>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                  <Field value={first} onChange={e => setFirst(e.target.value)} placeholder="Имя *" dark={dark}/>
                  <Field value={last} onChange={e => setLast(e.target.value)} placeholder="Фамилия" dark={dark}/>
                </div>
                <PwField value={pass} onChange={e => setPass(e.target.value)} placeholder="Пароль *" dark={dark} autocomplete="new-password" showStrength/>
                <PwField value={pass2} onChange={e => setPass2(e.target.value)} placeholder="Повторите пароль *" dark={dark} autocomplete="new-password"/>
                <SubmitBtn loading={loading} success={success} label="Создать аккаунт" loadingLabel="Создание..."/>
              </form>
            )}

            {/* Switch login/register */}
            {!isReg2 && (
              <div style={{ textAlign:'center', marginTop:2 }}>
                <span style={{ fontSize:13, color:muted }}>
                  {isLogin ? 'Нет аккаунта? ' : 'Уже есть аккаунт? '}
                </span>
                <button onClick={() => { setMode(isLogin ? 'register' : 'login'); setErr(''); setSuccess(false) }} style={{
                  background:'none', border:'none', color:'#0FA3B1',
                  fontSize:13, fontWeight:700, cursor:'pointer',
                  fontFamily:"'Manrope',sans-serif", padding:0, transition:'opacity .2s',
                }}
                onMouseEnter={e => e.currentTarget.style.opacity='.7'}
                onMouseLeave={e => e.currentTarget.style.opacity='1'}>
                  {isLogin ? 'Зарегистрироваться' : 'Войти'}
                </button>
              </div>
            )}
          </div>

          {/* Footer */}
          <div style={{
            borderTop:`1px solid ${line}`,
            padding:'14px 28px 16px',
          }}>
            <div style={{ textAlign:'center', fontSize:12, color:muted, lineHeight:1.5 }}>
              Продолжая, вы принимаете{' '}
              <span style={{ color:'#0FA3B1', cursor:'pointer', transition:'opacity .2s' }}
                onClick={() => navigate('/terms')}
                onMouseEnter={e => e.currentTarget.style.opacity='.7'}
                onMouseLeave={e => e.currentTarget.style.opacity='1'}>
                Условия использования
              </span>
              {' '}и{' '}
              <span style={{ color:'#0FA3B1', cursor:'pointer', transition:'opacity .2s' }}
                onClick={() => navigate('/privacy')}
                onMouseEnter={e => e.currentTarget.style.opacity='.7'}
                onMouseLeave={e => e.currentTarget.style.opacity='1'}>
                Политику конфиденциальности
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
